import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Search,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { TestService } from "@/services/test/testService";
import { VehicleTest } from "@/types/vehicleTest";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";

interface VehicleSelectionStepProps {
  onVehicleSelect: (vehicle: VehicleTest) => void;
  onCancel?: () => void;
  isStandalone?: boolean;
}

export const VehicleSelectionStep = ({
  onVehicleSelect,
  onCancel,
  isStandalone = false,
}: VehicleSelectionStepProps) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Search query for API (only updated when "Sök" is clicked)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch approved tests with server-side pagination and search
  const {
    data,
    isLoading: isLoadingAdminTests,
    refetch,
  } = useQuery({
    queryKey: [
      "admin-tests",
      "approved",
      currentPage,
      itemsPerPage,
      searchQuery,
    ],
    queryFn: async () => {
      return await TestService.getAllTestsForAdmin(
        currentPage,
        itemsPerPage,
        "approved",
        searchQuery || undefined,
      );
    },
    enabled: true, // Always enabled - fetch on mount
  });

  const approvedTests = data?.tests || [];
  const totalTests = data?.total || 0;
  const totalPages = Math.ceil(totalTests / itemsPerPage);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Load initial 10 approved tests when dialog opens
  useEffect(() => {
    if (currentPage === 1 && !searchQuery) {
      refetch();
    }
  }, []); // Only on mount

  const handleSearch = useCallback(() => {
    setSearchQuery(vehicleSearchQuery);
    setCurrentPage(1);
  }, [vehicleSearchQuery]);

  const handleInputFocus = useCallback(() => {
    // Load initial 10 approved tests when input is focused (if not already loaded)
    if (currentPage === 1 && !searchQuery && approvedTests.length === 0) {
      refetch();
    }
  }, [currentPage, searchQuery, approvedTests.length, refetch]);

  const handleVehicleSelect = (test: VehicleTest) => {
    onVehicleSelect(test);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">
          {L.selectVehicle ?? "Välj fordon"}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {(
            L.selectVehicleInstruction ??
            "Välj ett fordon med godkänt fordonstest för att skapa en annons. Hittar du inte det fordon du söker efter? Kika i fordonstest vyn och se till att fordonstestet är godkänt!"
          ).replace(/\*\*([^*]+)\*\*/g, "$1")}
        </p>
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 mb-4">
          <p className="text-sm font-bold text-orange-800 dark:text-orange-200">
            {L.approvedTestNotArchived ??
              "OBS! Det godkända testet får ej vara arkiverat! Så se till att avarkivera om så är fallet!"}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder={
                L.searchVehiclePlaceholder ??
                "Sök på registreringsnummer eller fordon..."
              }
              className="pl-10"
              value={vehicleSearchQuery}
              onChange={(e) => setVehicleSearchQuery(e.target.value)}
              onFocus={handleInputFocus}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </div>
          <Button onClick={handleSearch} variant="default">
            {L.search ?? "Sök"}
          </Button>
        </div>

        {isLoadingAdminTests ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              {L.loadingVehicleTests ?? "Laddar fordonstester..."}
            </p>
          </div>
        ) : approvedTests.length > 0 ? (
          <div className="space-y-3">
            {/* Vehicle list with fixed height and scrolling */}
            <div className="max-h-80 overflow-y-auto border rounded-lg p-2">
              {approvedTests.map((test) => (
                <Card
                  key={test.id}
                  className="p-4 cursor-pointer transition-colors hover:bg-accent/20 mb-2"
                  onClick={() => handleVehicleSelect(test)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">
                          {test.vehicleDetails.regNumber}
                        </h4>
                        <Badge
                          variant="default"
                          className="bg-green-100 text-green-800"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {L.approved ?? "Godkänd"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {test.vehicleName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {L.price ?? "Pris"}: {test.price} kr •{" "}
                        {L.created ?? "Skapad"}:{" "}
                        {new Date(test.createdAt).toLocaleDateString(
                          language === "en" ? "en-GB" : "sv-SE",
                        )}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {L.showingXofYVehicles
                    ?.replace(
                      "{start}",
                      ((currentPage - 1) * itemsPerPage + 1).toString(),
                    )
                    ?.replace(
                      "{end}",
                      Math.min(
                        currentPage * itemsPerPage,
                        totalTests,
                      ).toString(),
                    )
                    ?.replace("{total}", totalTests.toString()) ??
                    `Visar ${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalTests)} av ${totalTests} fordon`}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newPage = Math.max(1, currentPage - 1);
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === 1}
                  >
                    {L.previous ?? "Föregående"}
                  </Button>
                  <span className="text-sm">
                    {L.pageXofY
                      ?.replace("{current}", currentPage.toString())
                      ?.replace("{total}", totalPages.toString()) ??
                      `Sida ${currentPage} av ${totalPages}`}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newPage = Math.min(totalPages, currentPage + 1);
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === totalPages}
                  >
                    {L.next ?? "Nästa"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : searchQuery ? (
          <div className="text-center py-8">
            <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {L.noApprovedTestsFound?.replace("{query}", searchQuery) ??
                `Inga godkända fordonstester hittades för "${searchQuery}"`}
            </p>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {L.noApprovedTestsAvailable ??
                "Inga godkända fordonstester tillgängliga"}
            </p>
          </div>
        )}
      </div>

      {/* Cancel button for standalone mode */}
      {isStandalone && onCancel && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={onCancel}>
            {L.cancel ?? "Avbryt"}
          </Button>
        </div>
      )}
    </div>
  );
};
